import { ArrowUpRight } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { LocaleLink } from '@/components/LocaleLink'

import { cn } from '@/utilities/ui'

import { SectionHeading, type SectionHeadingLevel } from './common'

import { HomeBiNewsStoryRow } from './HomeBiNewsStoryRow'
import { HomeFooterLoadMore } from './HomeFooterLoadMore'
import { HOME_BI_SECTION, HOME_BI_SECTION_HEADING } from './home-bi-layout'
import type { FooterNewsItem, HomeFooterLoadMoreConfig } from './types'

export type HomeFooterDestaqueProps = {
  items: FooterNewsItem[]
  className?: string
  sectionHeading?: string
  sectionHeadingLevel?: SectionHeadingLevel
  showSectionHeader?: boolean
  showMoreLink?: boolean
  moreTopicLabel?: string
  moreHref?: string
  loadMore?: HomeFooterLoadMoreConfig
  showThumbnail?: boolean
}

/** Faixa `home-footer`: header + lista editorial única (título | imagem grande). */
export function HomeFooterDestaque({
  items,
  className,
  sectionHeading,
  sectionHeadingLevel = 'h2',
  showSectionHeader = true,
  showMoreLink = true,
  moreTopicLabel,
  moreHref = '#arquivo',
  loadMore,
  showThumbnail = true,
}: HomeFooterDestaqueProps) {
  const t = useTranslations('Home')
  if (!items || items.length === 0) return null
  const heading = sectionHeading || t('editorialArchive')
  const sectionId = HOME_BI_SECTION.footer
  const headingId = 'home-footer-news-heading'

  return (
    <div
      data-region="home-footer-destaque"
      data-home-section={sectionId}
      aria-labelledby={showSectionHeader ? headingId : undefined}
      className={cn('min-w-0 w-full', className)}
    >
      {showSectionHeader ? (
        <header className="section-heading flex items-center justify-between gap-4 lg:pr-5 xl:pr-6">
          <SectionHeading
            level={sectionHeadingLevel}
            id={headingId}
            className={cn('heading min-w-0 pr-3', HOME_BI_SECTION_HEADING)}
          >
            {heading}
          </SectionHeading>
          {showMoreLink ? (
            <LocaleLink
              href={moreHref}
              data-component-type="cta-more-link-arrow"
              className="cta-more-link-arrow group inline-flex h-8 min-h-8 shrink-0 items-center gap-2 rounded-sm py-0 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
            >
              <span className="link-text text-xs leading-none font-semibold tracking-wide whitespace-nowrap">
                {t('viewMore')}
                {moreTopicLabel ? (
                  <span className="link-text-label">&nbsp;{moreTopicLabel}</span>
                ) : null}
              </span>
              <span
                className="icon-wrapper bg-foreground text-background flex size-6 shrink-0 items-center justify-center rounded-full transition-opacity group-hover:opacity-90"
                aria-hidden
              >
                <ArrowUpRight className="size-3" strokeWidth={2.25} />
              </span>
            </LocaleLink>
          ) : null}
        </header>
      ) : null}

      <div
        data-home-column="news-list"
        data-home-section={sectionId}
        className="min-w-0 pt-4 lg:pr-5 lg:pt-5 xl:pr-6"
      >
        <div className="flex w-full min-w-0 flex-col">
          {items.map((item, index) => (
            <HomeBiNewsStoryRow
              key={`${item.href}-${index}`}
              item={item}
              index={index}
              variant="editorial"
              showThumbnail={showThumbnail}
            />
          ))}
        </div>
        {loadMore ? <HomeFooterLoadMore {...loadMore} startIndex={items.length} /> : null}
      </div>
    </div>
  )
}
